<template>
  <div class="select-database">
    <h3>{{ label }}</h3>
    <div class="input-group">
      <GeneralDropdown
        className="select-operation"
        @onDropdownOpen="onDatabaseDropdownOpen"
        @onDropdownClose="onDatabaseDropdownClose"
        :disabled="!isUserHasAccess"
      >
        <template v-slot:selectFilter>
          <input
            ref="select-database-input"
            class="select-database-input"
            :value="getDatabaseValue"
            @input="onDatabaseSearch"
            @keydown="onKeyDown"
            :placeholder="placeholder"
          />
        </template>
        <template v-slot:optionData>
          <b-dropdown-item
            v-for="(database, index) in filterDataBase"
            :key="database?.name"
            @click="onDatabaseSelect(database)"
            :active="database?.name === selectedDatabaseName"
            :class="[
              `${componentName}-database-dropdown-items`,
              { highlight: index === highlightedIndex },
              { 'is-overflowed': filterDataBase.length >= 7 },
            ]"
          >
            <div
              @mousemove="setHighlightedIndex(index)"
              class="database-caption"
            >
              {{ database.caption }}
            </div>
          </b-dropdown-item>
          <b-dropdown-item v-if="filterDataBase.length === 0" class="no-items">
            Keine Einträge gefunden
          </b-dropdown-item>
        </template>
        <template v-slot:iconContainer>
          <div
            class="close-btn"
            v-if="isShowRemoveIcon"
            @click.stop="onRemoveSelectedDatabase"
          ></div>
        </template>
      </GeneralDropdown>
      <div v-if="selectedDatabaseName" class="link-icon-wrap">
        <img
          @click="onClickRedirectToDatabase"
          :src="require('@/assets/img/url-link-icon.svg')"
          class="link-icon"
        />
      </div>
    </div>
  </div>
</template>

<script>
import GeneralDropdown from '../components/core/general-dropdown.vue';

export default {
  name: 'SelectDatabase',
  data() {
    return {
      searchDatabase: null,
      highlightedIndex: -1,
    };
  },
  props: {
    selectedDatabaseCaption: {
      type: String | null,
      required: true,
    },
    selectedDatabaseName: {
      type: String | null,
      required: true,
    },
    userTables: {
      type: Array,
      required: true,
    },
    componentName: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      required: false,
      default: 'DATENBANK',
    },
    isUserHasAccess: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  methods: {
    onDatabaseSelect(database) {
      this.$emit('onDatabaseSelect', database);
      this.searchDatabase = null;
    },
    onDatabaseDropdownOpen() {
      setTimeout(() => {
        this.$refs['select-database-input']?.focus();
        this.$refs['select-database-input']?.setSelectionRange(0, 0);
        const findIndex = this.filterDataBase.findIndex(
          (database) => database?.name === this.selectedDatabaseName
        );
        if (findIndex !== -1) {
          this.setHighlightedIndex(findIndex);
          this.scrollToHighlightedDatabaseItem();
        }
      }, 0);
    },
    onDatabaseSearch(e) {
      if (this.searchDatabase === null && this.getDatabaseValue) {
        const findDifference = this.compareTwoString(
          e.target.value,
          this.getDatabaseValue
        );
        this.searchDatabase = findDifference ? findDifference : '';
      } else {
        this.searchDatabase = e.target.value;
      }
      this.setHighlightedIndex(-1);
    },
    onDatabaseDropdownClose() {
      this.searchDatabase = null;
    },
    compareTwoString(str1, str2) {
      for (let i = 0; i < str1.length; i++) {
        const char1 = str1[i];
        const char2 = str2[i];
        if (char1 !== char2) {
          return char1;
        }
      }
    },
    onKeyDown(e) {
      const upKeyPress = e.keyCode === 38;
      const downKeyPress = e.keyCode === 40;
      const spaceKeyPress = e.keyCode === 32;
      if (
        upKeyPress ||
        downKeyPress ||
        spaceKeyPress ||
        (e.keyCode === 13 && this.filterDataBase.length === 0)
      ) {
        e.stopPropagation();
        e.preventDefault();
        const position = this.$refs['select-database-input']?.selectionStart;
        if (spaceKeyPress && this.searchDatabase !== null) {
          const searchDatabase =
            this.searchDatabase === null ? '' : this.searchDatabase;
          this.searchDatabase =
            searchDatabase.slice(0, position) +
            ' ' +
            searchDatabase.slice(position);
          this.$nextTick(() => {
            this.$refs['select-database-input']?.setSelectionRange(
              position + 1,
              position + 1
            );
          });
        }
      }
      if ((upKeyPress || downKeyPress) && this.filterDataBase.length) {
        if (this.highlightedIndex === -1) {
          this.setHighlightedIndex(0);
          return;
        } else if (upKeyPress) {
          this.setHighlightedIndex(
            this.highlightedIndex === 0
              ? this.filterDataBase.length - 1
              : this.highlightedIndex - 1
          );

          this.scrollToHighlightedDatabaseItem();
        } else if (downKeyPress) {
          this.setHighlightedIndex(
            this.highlightedIndex === this.filterDataBase.length - 1
              ? 0
              : this.highlightedIndex + 1
          );
        }
        this.scrollToHighlightedDatabaseItem();
      } else if (e.keyCode === 13) {
        if (this.filterDataBase.length === 0) {
          return;
        }
        const selectedDatabase = this.filterDataBase[this.highlightedIndex];
        this.onDatabaseSelect(selectedDatabase);
      }
    },
    scrollToHighlightedDatabaseItem() {
      const items = document.querySelectorAll(
        `.${this.componentName}-database-dropdown-items`
      );
      const highlightedItem = items[this.highlightedIndex];
      if (highlightedItem) {
        highlightedItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    },
    setHighlightedIndex(index) {
      this.highlightedIndex = index;
    },
    onRemoveSelectedDatabase() {
      this.$emit('onRemoveSelectedDatabase');
    },
    onClickRedirectToDatabase() {
      if (!this.selectedDatabaseName.length) return;
      const url = this.$router.resolve({
        path: '',
        query: { section: 'data', database: this.selectedDatabaseName },
      }).href;

      window.open(url, '_blank');
    },
  },
  computed: {
    filterDataBase() {
      if (this.searchDatabase) {
        return this.userTables.filter((table) =>
          table.caption
            .toLowerCase()
            .includes(this.searchDatabase.toLowerCase())
        );
      }
      return this.userTables;
    },
    getDatabaseValue() {
      if (this.searchDatabase !== null) {
        return this.searchDatabase;
      }
      return this.selectedDatabaseCaption;
    },
    isShowRemoveIcon() {
      return this.selectedDatabaseName && this.componentName === 'update-item';
    },
  },
  components: {
    GeneralDropdown,
  },
};
</script>

<style scoped>
@import 'https://use.fontawesome.com/releases/v5.6.3/css/all.css';

.select-database h3 {
  color: rgba(85, 93, 109, 0.4);
  font-size: 11px;
  letter-spacing: 0.03em;
  margin-bottom: 10px;
  margin-top: 10px;
}

.select-database .input-group {
  display: flex;
  flex-flow: row;
  gap: 10px;
}
.select-database li {
  font-size: 14px;
  line-height: 25px;
  color: #7e8890;
}

.select-database .select-operation {
  background-color: #ffffff;
  border: 1px solid #dee3e9;
  height: 50px;
  width: 100%;
}
.select-database .select-database-input {
  width: 100%;
}
.select-database .select-database-input:focus {
  outline: none;
}

.select-database .select-operation ::v-deep button {
  justify-content: space-between;
}
.select-database .input-attributes ::v-deep .customDropdown {
  padding-bottom: 20px;
}

.select-database .select-operation ::v-deep .dropdown-menu {
  width: 100% !important;
}
.select-database ::v-deep .dropdown-menu .no-items {
  pointer-events: none;
  padding: 10px;
}

.select-database ::v-deep .dropdown-menu .dropdown-item.active {
  background-color: #7e8890 !important;
  color: #fff !important;
}
.select-database
  ::v-deep
  .dropdown-menu
  li:not(.highlight):hover
  a:not(.active) {
  background: inherit !important;
  color: inherit !important;
}
.select-database ::v-deep .dropdown-menu li.highlight a:not(.active) {
  background: #f0f0f0;
  color: #212529;
}
.select-database ::v-deep .dropdown-menu li a {
  padding: 0 !important;
  border-radius: 5px;
}
.select-database ::v-deep .dropdown-menu li .database-caption {
  padding: 10px;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.select-database ::v-deep .dropdown-menu .is-overflowed {
  padding-left: 7px !important;
  border-radius: 10px;
}
.select-database .link-icon-wrap:hover {
  background: #f1f1f1;
}
.select-database .link-icon-wrap {
  width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f6f5f8;
  border-radius: 5px !important;
  height: 37px;
}
.select-database .link-icon-wrap img {
  cursor: pointer;
  margin: 13px;
}
.select-database .close-btn {
  position: absolute;
  right: 40px;
  background-image: url(../assets/img/work-flow-icons/close.svg);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  width: 12px;
  height: 12px;
  cursor: pointer;
}
</style>
